import { colorCssVariables, keyboard, semanticColors } from '@perplexicon/design-tokens';

import { assetUrl, assetVariantUrl } from '../assets/asset-manifest.js';
import type { AssetName } from '../assets/asset-manifest.js';

/** `keyboard` token key -> the CSS custom property `.px-keyboard` reads it from. */
const KEYBOARD_CSS_VARIABLES: Record<keyof typeof keyboard, string> = {
  maxWidth: '--px-kbd-max-width',
  columnGap: '--px-kbd-column-gap',
  rowGap: '--px-kbd-row-gap',
  keyMinWidth: '--px-kbd-key-min-width',
  keyHeight: '--px-kbd-key-height',
  radius: '--px-kbd-radius',
  fontSize: '--px-kbd-font-size',
  actionFontSize: '--px-kbd-action-font-size',
};

/**
 * Installs global semantic defaults before React renders. Game roots may
 * override the documented subset of these variables without platform code
 * knowing a game ID.
 */
export function installGlobalTheme(root: HTMLElement = document.documentElement): void {
  for (const [token, value] of Object.entries(semanticColors)) {
    root.style.setProperty(colorCssVariables[token as keyof typeof colorCssVariables], value);
  }
  for (const [token, value] of Object.entries(keyboard)) {
    root.style.setProperty(KEYBOARD_CSS_VARIABLES[token as keyof typeof keyboard], value);
  }
  root.style.setProperty('--px-asset-app-background', `url("${assetUrl('appBackground')}")`);
  const mobileBackground = assetVariantUrl('appBackground', '(max-width: 639px)');
  root.style.setProperty(
    '--px-asset-app-background-mobile',
    `url("${mobileBackground ?? assetUrl('appBackground')}")`,
  );

  // Decorative shared-UI textures and 9-slice button faces, resolved from the
  // asset manifest so `styles/index.css` never hardcodes a public path
  // (docs/UI_GUIDELINES.md §11).
  const assetVars: Record<string, AssetName> = {
    '--px-asset-parchment': 'textureParchment',
    '--px-asset-green-texture': 'textureGreen',
    '--px-asset-btn-primary': 'uiButtonPrimary',
    '--px-asset-btn-primary-hover': 'uiButtonPrimaryHover',
    '--px-asset-btn-primary-pressed': 'uiButtonPrimaryPressed',
    '--px-asset-btn-primary-disabled': 'uiButtonPrimaryDisabled',
    '--px-asset-btn-secondary': 'uiButtonSecondary',
  };
  for (const [cssVar, assetName] of Object.entries(assetVars)) {
    root.style.setProperty(cssVar, `url("${assetUrl(assetName)}")`);
  }
}
